import { Component, Input, OnDestroy, OnInit, QueryList, ViewChildren } from '@angular/core';
import { UntypedFormControl, UntypedFormGroup } from '@angular/forms';
import { Subscription } from 'rxjs';
import { DocusignComponent } from './docusign/docusign.component';
import { ISignatureProviderComponent } from './isignatureprovidercomponent';
import { NoSignatureProviderComponent } from './none/no-signature-provider.component';

/**
 * Signature Credentials tab of the AwesomeDocs config dialog.
 *
 * Owns the provider picker and the swapping of the selected provider's credentials
 * FormGroup into the `credentials` slot of the `signature` sub-group it is given.
 * Adv2ConfigComponent remains responsible for loading and saving.
 */
@Component({
  selector: 'app-signature-tab',
  templateUrl: './signature-tab.component.html',
  styleUrls: ['./signature-tab.component.scss'],
  standalone: false,
})
export class SignatureTabComponent implements OnInit, OnDestroy {
  @Input() formGroup: UntypedFormGroup;

  @ViewChildren('signatureProviderComponent')
  signatureProviderComponents: QueryList<ISignatureProviderComponent>;

  signatureProviders: { name: string; label: string }[] = [
    {
      name: NoSignatureProviderComponent.NAME,
      label: NoSignatureProviderComponent.LABEL,
    },
    { name: DocusignComponent.NAME, label: DocusignComponent.LABEL },
  ];

  private providerSubscription: Subscription;

  get signatureProvider(): UntypedFormControl {
    return this.formGroup.get('signatureProvider') as UntypedFormControl;
  }

  get errorNotificationEmailControl(): UntypedFormControl {
    return this.formGroup.get('errorNotificationEmail') as UntypedFormControl;
  }

  ngOnInit(): void {
    // every emission here is a real user selection - initialise() seeds the control
    // without emitting, so there is no initial event to skip
    this.providerSubscription = this.signatureProvider.valueChanges.subscribe((providerName) => {
      const sp = this.getSignatureProviderComponent(providerName);
      this.setCredentials(sp.formGroup);
    });
  }

  ngOnDestroy(): void {
    this.providerSubscription?.unsubscribe();
  }

  /**
   * Seeds the tab from the loaded config. Called by Adv2ConfigComponent once both
   * the config has arrived and this component's view has been created.
   */
  initialise(providerName: string, signature: unknown): void {
    // seeded silently: the credentials are swapped in directly below, so letting this
    // emit would make the subscription do the same work with the un-initialised group
    this.signatureProvider.setValue(providerName ?? NoSignatureProviderComponent.NAME, { emitEvent: false });

    const sp = this.getSignatureProviderComponent(providerName);
    this.setCredentials(sp.initialiseForm(signature));
  }

  private getSignatureProviderComponent(providerName: string): ISignatureProviderComponent {
    return (
      this.signatureProviderComponents.find((i) => i.name === providerName) ??
      this.signatureProviderComponents.find((i) => i.name === NoSignatureProviderComponent.NAME)
    );
  }

  private setCredentials(credentials: UntypedFormGroup): void {
    this.formGroup.removeControl('credentials');
    this.formGroup.addControl('credentials', credentials);
  }
}
